import * as React from 'react';
import { CheckCircle2, XCircle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

export type ToastVariant = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  title: string;
  description?: string;
  variant?: ToastVariant;
  durationMs?: number;
}

interface ToastContextValue {
  toast: (input: Omit<Toast, 'id'>) => void;
}

const ToastContext = React.createContext<ToastContextValue | null>(null);

export function useToast(): ToastContextValue {
  const ctx = React.useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}

export function ToastProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const [toasts, setToasts] = React.useState<Toast[]>([]);

  const dismiss = React.useCallback((id: number) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = React.useCallback<ToastContextValue['toast']>(
    (input) => {
      const id = Date.now() + Math.random();
      const t: Toast = { id, variant: 'info', durationMs: 3500, ...input };
      setToasts((prev) => [...prev, t]);
      window.setTimeout(() => dismiss(id), t.durationMs);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[100] flex flex-col gap-2">
        {toasts.map((t) => (
          <ToastView key={t.id} toast={t} onClose={() => dismiss(t.id)} />
        ))}
      </div>
    </ToastContext.Provider>
  );
}

function ToastView({ toast, onClose }: { toast: Toast; onClose: () => void }): JSX.Element {
  const Icon = toast.variant === 'success' ? CheckCircle2 : toast.variant === 'error' ? XCircle : null;
  const tone =
    toast.variant === 'success'
      ? 'border-success/30 bg-success/10 text-success-foreground'
      : toast.variant === 'error'
        ? 'border-destructive/30 bg-destructive/10 text-destructive-foreground'
        : 'border-border bg-popover text-popover-foreground';
  const iconClass =
    toast.variant === 'success'
      ? 'text-success'
      : toast.variant === 'error'
        ? 'text-destructive'
        : 'text-foreground';
  return (
    <div
      className={cn(
        'pointer-events-auto flex w-80 items-start gap-3 rounded-md border p-3 shadow-lg',
        tone,
      )}
    >
      {Icon && <Icon className={cn('mt-0.5 h-4 w-4 shrink-0', iconClass)} />}
      <div className="flex-1 space-y-0.5">
        <div className="text-sm font-medium text-foreground">{toast.title}</div>
        {toast.description && (
          <div className="text-xs text-muted-foreground">{toast.description}</div>
        )}
      </div>
      <button
        onClick={onClose}
        className="text-muted-foreground hover:text-foreground"
        aria-label="close"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
